/**
 * Atelier motion: section heading reveals, catalogue card reveals, magnetic calls to action and a
 * gentle 3D tilt of product card media. Uses the vendored GSAP + ScrollTrigger (window.gsap /
 * window.ScrollTrigger, loaded before this module). Everything runs inside gsap.matchMedia() so
 * reduced-motion users get no animation and all tweens and triggers revert cleanly when the media state changes.
 *
 * Horizon scrolls `.page-wrapper` instead of the document from 990px up, so ScrollTrigger is told
 * which element scrolls. No scroll hijacking: native scrolling stays untouched.
 */

const gsap = /** @type {any} */ (window).gsap;
const ScrollTrigger = /** @type {any} */ (window).ScrollTrigger;

/** The element that actually scrolls the page (Horizon desktop: .page-wrapper). */
function getScroller() {
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper instanceof HTMLElement) {
    const overflow = getComputedStyle(wrapper).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && wrapper.scrollHeight > wrapper.clientHeight) return wrapper;
  }
  return window;
}

/** Section headings below the first section, plus the editorial story heading. */
const HEADING_SELECTOR = [
  'main > .shopify-section:not(:first-child) h2',
  '.editorial-story__heading',
].join(',');

const CARD_SELECTOR = '.product-grid__item, .resource-list__item, .artist-approval';

const LENS_SELECTOR = '.editorial-story__photo, comparison-slider-component, .comparison-slider';

const MAGNETIC_SELECTOR = '[data-magnetic], .hero .button-custom, .hero .button, .concierge__button, .giveaway__teaser';

/** Product card media that tilts towards the pointer (desktop only). */
const TILT_SELECTOR = 'product-card .card-gallery';

function setupReveals() {
  const scroller = getScroller();
  ScrollTrigger.defaults({ scroller });

  /** @type {HTMLElement[]} */
  const headings = gsap.utils.toArray(HEADING_SELECTOR);
  for (const heading of headings) {
    // Cinematic line reveal: the heading rises out of a clip as it enters.
    gsap.fromTo(
      heading,
      { clipPath: 'inset(0 0 100% 0)', yPercent: 35 },
      {
        clipPath: 'inset(0 0 0% 0)',
        yPercent: 0,
        duration: 1.1,
        ease: 'power4.out',
        clearProps: 'clipPath,transform',
        scrollTrigger: { trigger: heading, start: 'top 88%', once: true },
      }
    );
  }

  // Lens focus: key media open from a rounded aperture, like an iris settling into focus.
  /** @type {HTMLElement[]} */
  const lenses = gsap.utils.toArray(LENS_SELECTOR);
  for (const media of lenses) {
    const finalRadius = getComputedStyle(media).borderRadius || '0px';
    gsap.fromTo(
      media,
      { clipPath: 'inset(12% 12% 12% 12% round 120px)' },
      {
        clipPath: `inset(0% 0% 0% 0% round ${finalRadius.split(' ').length > 1 ? finalRadius : finalRadius === '0px' ? '24px' : finalRadius})`,
        duration: 1.4,
        ease: 'expo.out',
        clearProps: 'clipPath',
        scrollTrigger: { trigger: media, start: 'top 85%', once: true },
      }
    );
  }

  const cards = gsap.utils.toArray(CARD_SELECTOR);
  if (cards.length) {
    // Cards only hide at the moment they animate in, so a missed trigger can never leave them invisible.
    ScrollTrigger.batch(cards, {
      start: 'top 92%',
      once: true,
      onEnter: (/** @type {Element[]} */ batch) =>
        gsap.from(batch, { autoAlpha: 0, y: 32, duration: 0.9, ease: 'power3.out', stagger: 0.08, clearProps: 'transform,visibility,opacity' }),
    });
  }

  // Layout settles after fonts and images; recalculate trigger positions once.
  const refresh = () => ScrollTrigger.refresh();
  document.fonts?.ready.then(refresh);
  window.addEventListener('load', refresh, { once: true });
}

/** Magnetic pull towards the pointer, reusing one tween per axis (gsap.quickTo). */
function setupMagnetic() {
  /** @type {Array<() => void>} */
  const cleanups = [];
  for (const element of gsap.utils.toArray(MAGNETIC_SELECTOR)) {
    if (!(element instanceof HTMLElement)) continue;
    const strength = Number(element.dataset.magneticStrength) || 0.28;
    const xTo = gsap.quickTo(element, 'x', { duration: 0.5, ease: 'power3' });
    const yTo = gsap.quickTo(element, 'y', { duration: 0.5, ease: 'power3' });

    /** @param {PointerEvent} event */
    const move = (event) => {
      const rect = element.getBoundingClientRect();
      xTo((event.clientX - (rect.left + rect.width / 2)) * strength);
      yTo((event.clientY - (rect.top + rect.height / 2)) * strength);
    };
    const leave = () => {
      xTo(0);
      yTo(0);
    };
    element.addEventListener('pointermove', move);
    element.addEventListener('pointerleave', leave);
    cleanups.push(() => {
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerleave', leave);
      gsap.set(element, { clearProps: 'transform' });
    });
  }
  return () => cleanups.forEach((cleanup) => cleanup());
}

/** A gentle 3D tilt of product card media following the pointer (gsap.quickTo per axis). */
function setupTilt() {
  /** @type {Array<() => void>} */
  const cleanups = [];
  for (const element of gsap.utils.toArray(TILT_SELECTOR)) {
    if (!(element instanceof HTMLElement)) continue;
    const card = element.closest('product-card');
    if (!(card instanceof HTMLElement)) continue;
    gsap.set(element, { transformPerspective: 900, transformOrigin: '50% 50%' });
    const rotateX = gsap.quickTo(element, 'rotationX', { duration: 0.6, ease: 'power3' });
    const rotateY = gsap.quickTo(element, 'rotationY', { duration: 0.6, ease: 'power3' });

    /** @param {PointerEvent} event */
    const move = (event) => {
      const rect = element.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      rotateY(x * 7);
      rotateX(y * -7);
    };
    const leave = () => {
      rotateX(0);
      rotateY(0);
    };
    card.addEventListener('pointermove', move);
    card.addEventListener('pointerleave', leave);
    cleanups.push(() => {
      card.removeEventListener('pointermove', move);
      card.removeEventListener('pointerleave', leave);
      gsap.set(element, { clearProps: 'transform' });
    });
  }
  return () => cleanups.forEach((cleanup) => cleanup());
}

function init() {
  if (!gsap || !ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('has-atelier-motion');

  const mm = gsap.matchMedia();
  mm.add(
    {
      motion: '(prefers-reduced-motion: no-preference)',
      finePointer: '(prefers-reduced-motion: no-preference) and (hover: hover) and (pointer: fine)',
      desktop: '(min-width: 990px)',
    },
    (/** @type {any} */ context) => {
      const { motion, finePointer, desktop } = context.conditions;
      if (!motion) return;
      // Breakpoint changes swap the scroll container, so this run re-creates the triggers.
      setupReveals();
      if (!finePointer) return;
      const cleanMagnetic = setupMagnetic();
      const cleanTilt = desktop ? setupTilt() : () => {};
      return () => {
        cleanMagnetic();
        cleanTilt();
      };
    }
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
